<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { computed, ref, watch } from 'vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useMapGetter } from 'dashboard/composables/store';
import { fetchPendingReservationRequestCount } from 'dashboard/api/hospital/ddukdocReservations';

const { updateUISettings } = useUISettings();

// DDukDoc: Captain/Copilot is hidden on this fork (see
// features/modules/chat-dashboard/SPEC.md). The dedicated Captain pages were
// route-removed earlier; this also hides the in-conversation Copilot tab so it
// cannot reappear even if the CAPTAIN account feature flag is toggled on.
const showCopilotTab = computed(() => false);

const { uiSettings } = useUISettings();
const isContactSidebarOpen = computed(
  () => uiSettings.value.is_contact_sidebar_open
);
const isCopilotPanelOpen = computed(
  () => uiSettings.value.is_copilot_panel_open
);

const toggleConversationSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: !isContactSidebarOpen.value,
    is_copilot_panel_open: false,
  });
};

const handleConversationSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: true,
    is_copilot_panel_open: false,
  });
};

const handleCopilotSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: true,
  });
};

// DDukDoc: 이 대화에 승인 대기 예약 신청이 있으면 토글에 배지를 달고 연락처 패널을 연다.
// 패널이 접힌 기본 상태에서는 신청이 들어와도 눈에 띄지 않아 상담원이 승인 버튼을 못 찾았다.
const currentChat = useMapGetter('getSelectedChat');
const pendingReservationCount = ref(0);
let pendingSeq = 0;
watch(
  () => currentChat.value?.id,
  async conversationId => {
    pendingSeq += 1;
    const seq = pendingSeq;
    pendingReservationCount.value = 0;
    if (!conversationId) return;
    const count = await fetchPendingReservationRequestCount(conversationId);
    if (seq !== pendingSeq) return;
    pendingReservationCount.value = count;
    if (count > 0 && !isContactSidebarOpen.value) handleConversationSidebarToggle();
  },
  { immediate: true }
);

const keyboardEvents = {
  'Alt+KeyO': {
    action: toggleConversationSidebarToggle,
  },
};
useKeyboardEvents(keyboardEvents);
</script>

<template>
  <ButtonGroup
    class="flex flex-col justify-center items-center absolute top-36 xl:top-24 ltr:right-2 rtl:left-2 bg-n-solid-2/90 backdrop-blur-lg border border-n-weak/50 rounded-full gap-1.5 p-1.5 shadow-sm transition-shadow duration-200 hover:shadow"
  >
    <span class="relative inline-flex">
      <Button
        v-tooltip.top="$t('CONVERSATION.SIDEBAR.CONTACT')"
        ghost
        slate
        sm
        class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95 active:!brightness-105 active:duration-75"
        :class="{
          'bg-n-alpha-2 active:shadow-sm': isContactSidebarOpen,
        }"
        icon="i-ph-user-bold"
        @click="handleConversationSidebarToggle"
      />
      <span
        v-if="pendingReservationCount > 0"
        class="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-n-brand text-white text-[10px] leading-4 font-bold text-center pointer-events-none"
        :title="$t('HOSPITAL.RESERVATIONS.PENDING_BADGE', { count: pendingReservationCount })"
      >
        {{ pendingReservationCount }}
      </span>
    </span>
    <Button
      v-if="showCopilotTab"
      v-tooltip.bottom="$t('CONVERSATION.SIDEBAR.COPILOT')"
      ghost
      slate
      sm
      class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95 active:duration-75"
      :class="{
        'bg-n-alpha-2 !text-n-iris-9 active:!brightness-105 active:shadow-sm':
          isCopilotPanelOpen,
      }"
      icon="i-woot-captain"
      @click="handleCopilotSidebarToggle"
    />
  </ButtonGroup>
</template>
